import type { INovaLoadType, INovaWarehouse } from '@/interfaces/nova';
import type { NovaApiService } from '@/services/nova/NovaApiService';
import { getHeadquartersId } from '@/services/importHelpers';
import { cleanWhiteSpace } from '@/services/util';
import {
  ALREADY_EXISTS_MESSAGE,
  SUCCESS_IMPORT_MESSAGE,
  forEachSequentially,
  getFailureStatusMessage,
  wasAlreadyImported
} from './importShared';

type LoadTypeImportParams = {
  orgId: string;
  loadTypes: INovaLoadType[];
  apiService: NovaApiService;
  warehousesKeyedByExternalId: Record<string, INovaWarehouse>;
};

type LoadTypeImportState = {
  orgId: string;
  apiService: NovaApiService;
  warehousesKeyedByExternalId: Record<string, INovaWarehouse>;
  headquartersId: string | number | null | undefined;
  existingLoadTypes: INovaLoadType[];
  loadTypeIdsKeyedByExternalId: { [key: string]: string };
  loadTypesReport: INovaLoadType[];
};

function findExistingLoadType(
  loadType: INovaLoadType,
  loadTypeWarehouse: INovaWarehouse | undefined,
  isOrgLevel: boolean,
  existingLoadTypes: INovaLoadType[]
) {
  return existingLoadTypes.find(existingLoadType => {
    const doNameAndDirectionMatch =
      loadType.name === cleanWhiteSpace(existingLoadType.name) &&
      loadType.direction === existingLoadType.direction;

    if (isOrgLevel) {
      // Return the loadtype that has the same name and NO warehouseId
      // In this case it would be a org loadtype
      return doNameAndDirectionMatch && !existingLoadType.warehouseId;
    }

    // In this case we will match an existing nova warehouse
    // only if it has a valid warehouseId and the same warehouseId
    // as the imported one
    const isLocationMatch =
      loadTypeWarehouse &&
      Boolean(existingLoadType.warehouseId) &&
      existingLoadType.warehouseId === loadTypeWarehouse.id;
    return doNameAndDirectionMatch && isLocationMatch;
  });
}

async function createLoadType(
  loadType: INovaLoadType,
  loadTypeWarehouse: INovaWarehouse | undefined,
  isOrgLevel: boolean,
  { orgId, apiService, loadTypeIdsKeyedByExternalId, loadTypesReport }: LoadTypeImportState
) {
  loadType.orgId = orgId;
  try {
    if (!isOrgLevel) {
      if (!loadTypeWarehouse) {
        throw new Error(
          `Load Type ${loadType.name} (id: ${loadType.externalId}) is not assigned to an valid warehouse`
        );
      }
      loadType.warehouseId = loadTypeWarehouse.id as string;
    }
    const response = await apiService.createLoadType(loadType);
    const [newLoadType] = response.data;
    if (!newLoadType) {
      throw new Error('no load type was returned');
    }
    loadType.wasImportedIntoNova = SUCCESS_IMPORT_MESSAGE;
    loadType.id = newLoadType.id;
    loadTypesReport.push(loadType);
    if (loadType.externalId && newLoadType.id) {
      loadTypeIdsKeyedByExternalId[loadType.externalId] = newLoadType.id;
    }
  } catch (e) {
    loadType.wasImportedIntoNova = getFailureStatusMessage({ e });
    loadTypesReport.unshift(loadType);
  }
}

async function importLoadType(loadType: INovaLoadType, importState: LoadTypeImportState) {
  const { warehousesKeyedByExternalId, headquartersId, existingLoadTypes } = importState;
  const { loadTypeIdsKeyedByExternalId, loadTypesReport } = importState;
  loadType.name = cleanWhiteSpace(loadType.name);
  const importedId = loadType.id;
  if (wasAlreadyImported(loadType) && importedId) {
    loadType.wasImportedIntoNova = ALREADY_EXISTS_MESSAGE;
    loadTypesReport.push(loadType);
    loadTypeIdsKeyedByExternalId[loadType.externalId] = importedId;
    return;
  }
  const loadTypeWarehouse = warehousesKeyedByExternalId[loadType.externalWarehouseId];
  // TODO: Seeing pattern with checking existing entities here - explore abstraction
  const isOrgLevel =
    headquartersId === loadType.externalWarehouseId || !loadType.externalWarehouseId;
  const existingLoadType = findExistingLoadType(
    loadType,
    loadTypeWarehouse,
    isOrgLevel,
    existingLoadTypes
  );
  if (existingLoadType) {
    loadType.wasImportedIntoNova = ALREADY_EXISTS_MESSAGE;
    loadType.id = existingLoadType.id;
    loadType.warehouseId = existingLoadType.warehouseId;
    loadType.orgId = existingLoadType.orgId;
    loadTypesReport.push(loadType);
    if (loadType.externalId && existingLoadType.id) {
      loadTypeIdsKeyedByExternalId[loadType.externalId] = existingLoadType.id;
    }
    return;
  }

  await createLoadType(loadType, loadTypeWarehouse, isOrgLevel, importState);
}

export async function importLoadTypes({
  orgId,
  loadTypes,
  apiService,
  warehousesKeyedByExternalId
}: LoadTypeImportParams) {
  const existingLoadTypesRes = await apiService.getLoadTypes();
  const importState: LoadTypeImportState = {
    orgId,
    apiService,
    warehousesKeyedByExternalId,
    headquartersId: getHeadquartersId(warehousesKeyedByExternalId),
    existingLoadTypes: existingLoadTypesRes.data ?? [],
    loadTypeIdsKeyedByExternalId: {},
    loadTypesReport: []
  };

  await forEachSequentially(loadTypes, loadType => importLoadType(loadType, importState));

  return {
    loadTypeIdsKeyedByExternalId: importState.loadTypeIdsKeyedByExternalId,
    loadTypesReport: importState.loadTypesReport
  };
}
